import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

class QuickAddSanolaComponent extends Component {
  /** @type {HTMLButtonElement | null} */
  #activeButton = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | null} */
  #clearTimeoutId = null;
  /** @type {boolean} */
  #waitForDrawer = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleSwatchClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleSwatchClick);
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearLoadingState();
  }

  #handleSwatchClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-quick-add-sanola-size]');
    if (!(swatch instanceof HTMLButtonElement)) return;
    if (!this.contains(swatch)) return;
    if (swatch.disabled) return;

    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    const form = this.querySelector('form');
    const variantInput = form?.querySelector('input[name="id"]');

    if (!(form instanceof HTMLFormElement)) return;
    if (!(variantInput instanceof HTMLInputElement)) return;

    event.preventDefault();
    event.stopPropagation();

    this.#setLoadingState(swatch);
    this.#waitForDrawer = this.#shouldWaitForDrawer();

    variantInput.disabled = false;
    variantInput.value = variantId;

    form.requestSubmit();
  };

  #handleCartUpdate = () => {
    if (!this.#activeButton) return;
    if (this.#waitForDrawer) {
      const cartDrawer = this.#getCartDrawer();
      const dialog = cartDrawer?.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        this.#clearLoadingState();
      }
      return;
    }

    this.#clearLoadingState();
  };

  #handleDialogOpen = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('cart-drawer-component')) return;

    this.#clearLoadingState();
  };

  #shouldWaitForDrawer() {
    const cartDrawer = this.#getCartDrawer();
    return cartDrawer?.hasAttribute('auto-open') ?? false;
  }

  #getCartDrawer() {
    return /** @type {HTMLElement | null} */ (document.querySelector('cart-drawer-component'));
  }

  /** @param {HTMLButtonElement} button */
  #setLoadingState(button) {
    if (this.#activeButton && this.#activeButton !== button) {
      this.#activeButton.removeAttribute('data-loading');
      this.#activeButton.removeAttribute('aria-busy');
    }

    this.#activeButton = button;
    button.setAttribute('data-loading', 'true');
    button.setAttribute('aria-busy', 'true');
    this.setAttribute('data-loading', 'true');

    if (this.#clearTimeoutId) {
      window.clearTimeout(this.#clearTimeoutId);
    }

    this.#clearTimeoutId = window.setTimeout(() => {
      this.#clearLoadingState();
    }, 4000);
  }

  #clearLoadingState() {
    if (!this.#activeButton) return;

    this.#activeButton.removeAttribute('data-loading');
    this.#activeButton.removeAttribute('aria-busy');
    this.#activeButton = null;
    this.#waitForDrawer = false;
    this.removeAttribute('data-loading');

    if (this.#clearTimeoutId) {
      window.clearTimeout(this.#clearTimeoutId);
      this.#clearTimeoutId = null;
    }
  }
}

if (!customElements.get('quick-add-sanola-component')) {
  customElements.define('quick-add-sanola-component', QuickAddSanolaComponent);
}
